import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { CustomTableComponent } from '../../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../../shared/ui/custom-table/model/table-config.model';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import { mapTableState, tableStateEquals } from '../../../../shared/utils/table-state.utils';
import { TableStateSnapshot } from '../../../../shared/utils/table-types';

import { createCreditPolicyTableConfig, TableRowAction } from './credit-policy-table.config';
import { CreditPolicyFormDialogComponent } from '../credit-policy-form-dialog/credit-policy-form-dialog.component';
import { CreditPolicyOperationDialogComponent } from '../credit-policy-operation-dialog/credit-policy-operation-dialog.component';
import {
  CREDIT_POLICY_BANK_MOVEMENT,
  CREDIT_POLICY_CASH_MOVEMENT,
  CreditPolicyOperationRequest,
  CreditPolicyRow,
} from '../../models/credit-policy-operation.model';
import {
  CreditPolicyListSummary,
  CreditPolicyReadService,
  EMPTY_CREDIT_POLICY_SUMMARY,
} from '../../services/credit-policy-read.service';

import { Utils } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';
import { BankTypeEnum } from '../../../../../shared/data-types';

import { PolizaCreditoService } from '../../../../../services/finanzas/poliza-credito/poliza-credito.service';
import { ReconciliationConnectComponent } from '../../../../../modules/conciliation/connect/reconciliation-connect.component';
import { ReconciliationService } from '../../../../../modules/conciliation/reconciliation.service';
import { AccountingAccountsService } from '../../../../../modules/accounting/accounting-accounts/accounting-accounts.service';
import { AccountingAccountsModel } from '../../../../../modules/accounting/accounting-accounts/accounting-accounts.model';
import { AccountingAccountsDialogLedgerComponent } from '../../../../../modules/accounting/accounting-accounts/accounting-accounts-dialog-ledger.component';
import { BankTransactionImportImportComponent } from '../../../../../modules/conciliation/import/bank-transaction-import.component';
import { SubscriptionService } from '../../../../../modules/subscription/subscription.service';

@Component({
  standalone: false,
  selector: 'app-credit-policy-list-v2',
  templateUrl: './credit-policy-list.components.html',
  styleUrls: ['./credit-policy-list.components.css']
})
export class CreditPolicyListComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('table', { static: false }) t1!: CustomTableComponent;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;
  @ViewChild('creditPolicySummary') creditPolicySummary!: TemplateRef<any>;
  @ViewChild('expandDetail') expandDetailTpl!: TemplateRef<any>;

  tableConfig!: TableConfig;

  icon = 'account_balance';
  title = 'Pólizas de crédito';
  subTitle = 'Consulta tu límite, dispuesto y disponible, y registra constitución, liquidaciones y movimientos';

  summary: CreditPolicyListSummary = { ...EMPTY_CREDIT_POLICY_SUMMARY };
  conciliationActive = false;

  quickSearch = '';
  private readonly quickSearch$ = new Subject<string>();

  private rows: CreditPolicyRow[] = [];
  private connectedIds: string[] | null = null;
  private lastState: TableStateSnapshot | null = null;
  private loadSeq = 0;
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private reads: CreditPolicyReadService,
    private polizaCreditoService: PolizaCreditoService,
    private reconciliationService: ReconciliationService,
    private accountingAccountsService: AccountingAccountsService,
    private subscriptionService: SubscriptionService,
    private notify: NotificationService,
    private confirm: ConfirmService,
    private tier: TierService,
    private loadingDialog: LoadingDialogService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.tableConfig = createCreditPolicyTableConfig(
      {
        actionMap: {
          [TableRowAction.Edit]: (row) => this.edit(row),
          [TableRowAction.ConnectBank]: (row) => this.connectBank(row),
          [TableRowAction.Reconcile]: (row) => this.reconcile(row),
          [TableRowAction.Delete]: (row) => this.delete(row),
          [TableRowAction.Constitution]: (row) => this.openOperation(row, { kind: 'constitution' }),
          [TableRowAction.Liquidation]: (row) => this.openOperation(row, { kind: 'settlement' }),
          [TableRowAction.Traspaso]: (row) => this.openOperation(row, { kind: 'bank', movementType: CREDIT_POLICY_BANK_MOVEMENT.traspaso }),
          [TableRowAction.Transferencia]: (row) => this.openOperation(row, { kind: 'bank', movementType: CREDIT_POLICY_BANK_MOVEMENT.transferencia }),
          [TableRowAction.WithdrawCash]: (row) => this.openOperation(row, { kind: 'cash', movementType: CREDIT_POLICY_CASH_MOVEMENT.reintegro }),
          [TableRowAction.DepositCash]: (row) => this.openOperation(row, { kind: 'cash', movementType: CREDIT_POLICY_CASH_MOVEMENT.ingreso }),
          [TableRowAction.Ledger]: (row) => this.ledger(row),
          [TableRowAction.ImportTransactions]: (row) => this.importTransactions(row),
        },
        selectable: false,
        columns: [
          { key: 'banco', label: 'Banco', sortable: true, sortKey: 'banco', onCellClick: (row) => this.edit(row) },
          { key: 'iban', label: 'IBAN', sortable: true, sortKey: 'iban', ellipsis: true },
          { key: 'accountingModelsNumber', label: 'Cuenta contable', sortable: true, sortKey: 'accountingModelsNumber' },
          { key: 'limite', label: 'Límite', sortable: true, sortKey: 'limite', formatFn: (val) => this.money(val) },
          { key: 'dispuesto', label: 'Dispuesto', sortable: true, sortKey: 'dispuesto', formatFn: (val) => this.money(val) },
          { key: 'disponible', label: 'Disponible', sortable: true, sortKey: 'disponible', formatFn: (val) => this.money(val) },
          {
            key: 'connected', label: 'Conexión bancaria', sortable: false,
            badge: {
              variantFn: (val: boolean | null) => val === true ? 'success' : 'neutral',
              labelFn: (val: boolean | null) => val === true ? 'Conectada' : 'No conectada',
            },
          },
        ],
        headerActions: [
          { label: '', icon: 'add', type: 'button', callback: () => this.create() },
        ],
        gridActions: [],
        rowConfig: {},
      },
      {
        filterView: '',
      },
      {
        canConnectBank: () => this.conciliationActive || Utils.IsTierFive(),
        canReconcile: () => this.conciliationActive || Utils.IsTierFour(),
      }
    );

    this.tableConfig.rowConfig = {
      ...this.tableConfig.rowConfig,
      expandable: true,
      onExpand: () => { },
      onCollapse: () => { },
      onToggleExpand: () => { },
    };
  }

  ngAfterViewInit(): void {
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;
    this.tableConfig.summaryTemplate = this.creditPolicySummary;
    this.tableConfig.expandableRowTemplate = this.expandDetailTpl;

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$),
      )
      .subscribe(state => {
        const filters: any = state.filters || {};
        const search = String(filters.search || '');
        if (search !== this.quickSearch) this.quickSearch = search;
        this.lastState = state;
        this.load();
      });

    this.loadSubscription();
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(value: string): void {
    this.quickSearch = value || '';
    this.quickSearch$.next(this.quickSearch);
  }

  create(): void {
    if (!this.tier.requireAdvancedTier()) return;
    CreditPolicyFormDialogComponent.open(this.dialog, { uid: null })
      .subscribe(changed => { if (changed) this.load(); });
  }

  edit(row: CreditPolicyRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    CreditPolicyFormDialogComponent.open(this.dialog, { uid: row.uid, policy: row })
      .subscribe(changed => { if (changed) this.load(); });
  }

  delete(row: CreditPolicyRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.confirm.open({
      title: 'Eliminar póliza de crédito',
      subtitle: row.banco || undefined,
      message: 'Se eliminarán también su constitución, liquidaciones, traspasos, transferencias y movimientos de efectivo con sus asientos contables. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.policyDetails(row),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando póliza de crédito…',
      errorText: 'No se pudo eliminar la póliza de crédito',
      action: () => this.polizaCreditoService.delete(row.uid).then(ok => { if (ok === false) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Póliza de crédito eliminada correctamente');
      this.load();
    });
  }

  openOperation(row: CreditPolicyRow, request: CreditPolicyOperationRequest): void {
    if (!this.tier.requireAdvancedTier()) return;
    CreditPolicyOperationDialogComponent.open(this.dialog, {
      policy: row,
      kind: request.kind,
      movementType: request.movementType,
      editUid: request.editUid,
    }).subscribe(changed => { if (changed) this.load(); });
  }

  ledger(row: CreditPolicyRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    const accountNumber = row.accountingModelsNumber;
    if (!accountNumber) {
      this.notify.warning('Esta póliza no dispone de cuenta contable asociada.');
      return;
    }
    this.loadingDialog.run('Buscando cuenta contable…', this.accountingAccountsService.list(0, 1, accountNumber))
      .then((x: any) => {
        let accountingModels: AccountingAccountsModel = new AccountingAccountsModel();
        const list = (x && x.data) as AccountingAccountsModel[];
        if (list && list.length === 1) accountingModels = list[0];
        this.dialog.open(AccountingAccountsDialogLedgerComponent, {
          width: '700px',
          maxWidth: '96vw',
          maxHeight: '90vh',
          autoFocus: false,
          data: {
            id: accountingModels.id,
            name: accountingModels.name,
            number: accountingModels.number,
          }
        });
      })
      .catch(() => this.notify.error('No se pudo abrir el mayor contable'));
  }

  connectBank(row: CreditPolicyRow): void {
    if (!Utils.IsTierFive()) {
      this.notify.warning(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }
    this.dialog.open(ReconciliationConnectComponent, {
      data: {
        bankId: row.uid,
        bankTypeId: BankTypeEnum.Poliza,
        bankName: row.banco || '',
      }
    }).afterClosed().subscribe((bankId: string) => {
      if (bankId == null) return;
      this.connectedIds = null;
      this.load();
    });
  }

  reconcile(row: CreditPolicyRow): void {
    if (!this.conciliationActive && !this.tier.requireAdvancedTier()) return;
    this.router.navigate(['/finance/creditfacility/conciliation'], { queryParams: { bankId: row.uid, bankTypeId: BankTypeEnum.Poliza } });
  }

  importTransactions(row: CreditPolicyRow): void {
    if (!this.conciliationActive && !this.tier.requireAdvancedTier()) return;
    BankTransactionImportImportComponent.open(this.dialog, {
      bankId: row.uid,
      bankTypeId: BankTypeEnum.Poliza,
      bankName: row.banco ? 'Póliza de crédito · ' + row.banco : 'Póliza de crédito',
    }).subscribe();
  }

  reload(): void {
    this.load();
  }

  private loadSubscription(): void {
    this.subscriptionService.getActive().then(response => {
      if (response && response.length > 0) {
        this.conciliationActive = response.filter((c: any) => c.alias === 'SUBSCRIPTION_RECONCILIATION').length > 0;
      }
    }).catch(() => { this.conciliationActive = false; });
  }

  private load(): void {
    if (!this.t1 || !this.lastState) return;
    const state = this.lastState;
    const filters: any = state.filters || {};
    const seq = ++this.loadSeq;
    this.t1.tableState.setLoading(true);

    this.reads.list({
      page: (state.pageIndex || 0) + 1,
      pageSize: state.pageSize || 10,
      search: String(filters.search || '').trim(),
      sortField: state.sortField || '',
      sortDirection: state.sortDirection || '',
    })
      .then(result => {
        if (seq !== this.loadSeq) return;
        const expanded = new Set(this.rows.filter(row => row.__expanded).map(row => row.uid));
        this.rows = result.items.map(item => ({ ...item, __expanded: expanded.has(item.uid) }));
        this.summary = result.summary;
        this.applyConnections();
        this.t1.tableState.setData(this.rows);
        this.t1.tableState.setTotalItems(result.total);
        this.t1.tableState.setLoading(false);
        this.cdr.markForCheck();
        this.ensureConnections();
      })
      .catch(() => {
        if (seq !== this.loadSeq) return;
        this.t1.tableState.setLoading(false);
        this.notify.error('No se pudo cargar la lista de pólizas de crédito');
      });
  }

  private ensureConnections(): void {
    if (this.connectedIds !== null) return;
    this.connectedIds = [];
    this.reconciliationService.getAllBankStatusRequisition()
      .then(response => {
        this.connectedIds = Array.isArray(response) ? response.map((x: any) => x.bankId) : [];
        this.applyConnections();
        this.t1.tableState.setData(this.rows.slice());
        this.cdr.markForCheck();
      })
      .catch(() => { this.connectedIds = []; });
  }

  private applyConnections(): void {
    const ids = this.connectedIds;
    this.rows = this.rows.map(row => ({ ...row, connected: ids ? ids.indexOf(row.uid) !== -1 : false }));
  }

  private policyDetails(row: CreditPolicyRow): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (row.iban) details.push({ label: 'IBAN', value: row.iban });
    if (row.accountingModelsNumber) details.push({ label: 'Cuenta contable', value: row.accountingModelsNumber });
    details.push({ label: 'Límite', value: this.money(row.limite) });
    details.push({ label: 'Dispuesto', value: this.money(row.dispuesto) });
    return details;
  }

  private money(value: any): string {
    return Utils.FormatMoney(value ?? 0) + ' €';
  }
}
